import type { ReactNode } from 'react';
import ProvenanceGutter from './ProvenanceGutter';
import type { AgentKey } from './AgentMark';
import type { Fact } from './fact';
import { isAbsent } from './fact';
import { VERIFICATION_LABELS, formatQuantity, type LegendaLang } from './vocabulary';

export interface EvidenceCardProps<T extends number | string> {
  /** What the value measures, in the user's language. */
  parameter: string;
  fact: Fact<T>;
  agent: AgentKey;
  lang: LegendaLang;
  /** Decimal places for numeric values; omitted lets the locale decide. */
  fractionDigits?: number;
  /** Extra chips — sample id, method, review event. */
  meta?: ReactNode;
}

/**
 * A single archive-printed value with everything needed to audit it.
 *
 * Three things are structural rather than optional:
 *
 * 1. The value is set in the serif face. That is the archive's own voice —
 *    a number a page printed can never share a treatment with a sentence a
 *    model wrote.
 * 2. The raw→normalised line is the card's last row, always. A value whose
 *    printed form has been lost cannot be audited, so the printed form is
 *    never dropped from the display.
 * 3. Absence renders as the status label («нет данных»), in muted ink and with
 *    an empty verification channel — not as a dash, a blank, or an error.
 */
export default function EvidenceCard<T extends number | string>({
  parameter,
  fact,
  agent,
  lang,
  fractionDigits,
  meta,
}: EvidenceCardProps<T>) {
  const absent = isAbsent(fact);

  const rendered = (() => {
    if (absent) return VERIFICATION_LABELS.unknown[lang];
    if (typeof fact.normalized === 'number') {
      return formatQuantity(fact.normalized, lang, fractionDigits);
    }
    return String(fact.normalized);
  })();

  return (
    <article className="lg-ev">
      <ProvenanceGutter
        agent={agent}
        lang={lang}
        verification={fact.verification}
        citation={fact.citation}
      >
        <div className="lg-ev__param">{parameter}</div>

        <div className={absent ? 'lg-ev__value lg-muted' : 'lg-ev__value'}>
          <span className="lg-value">{rendered}</span>
          {!absent && fact.unit ? <span className="lg-ev__unit"> {fact.unit}</span> : null}
        </div>

        {meta ? <div className="lg-ev__meta">{meta}</div> : null}

        <div className="lg-ev__raw">
          {absent ? (
            <>регион доказательства отсутствует · запись сохранена как «нет данных»</>
          ) : (
            <>
              напечатано: «{fact.raw}» → нормализовано: {String(fact.normalized)}
              {fact.unit ? ` ${fact.unit}` : ''} · извлечение {fact.extractionRunId}
            </>
          )}
        </div>
      </ProvenanceGutter>
    </article>
  );
}
